/**
 * utils/importParser.js
 * Pure parser: turns extracted xlsx rows (SheetJS `sheet_to_json(ws, {header:1, raw:true})`
 * shape) into app-shaped data — items (no internalCode yet), categories, cars, a review list
 * for ambiguous market-code splits, and a list of sheets that produced no items at all. No
 * I/O, no code minting, no commit — see 3b for the UI/file-reading/minting/commit layer that
 * consumes this output.
 *
 * Input: sheets = [{ name: string, rows: any[][] }], rows exactly as SheetJS produces them
 * (strings, numbers, JS Date instances, null/undefined holes).
 *
 * Standard column layout (0-based; column 0 is unused in the source sheets):
 *   1 KODE BARANG · 2 JENIS BARANG · 3 TIPE MOBIL · 4 TGL · 5 NO. NOTA · 6 NAMA TOKO ·
 *   7 IN · 8 OUT · 9 SISA · 10 IJM · 11 KETERANGAN
 * The header row is DETECTED per sheet (first row whose cells include "KODE BARANG",
 * "SISA", AND "IJM", case-insensitive/trimmed), not hard-coded by index — CMB-style sheets
 * differ, and some sheets repeat the header row partway through for readability.
 *
 * Only MASTER rows (an item's opening/current state) are imported. Transaction rows
 * (anything with a TGL date) are skipped — their running balances are spreadsheet FORMULAS
 * in the export, not authoritative data; re-deriving them is out of scope here. History for
 * imported items starts fresh at commit (3b), same as any other newly created item.
 */
import { parseFitment } from "./fitment.js";

/** Sheets to never import, regardless of layout (trim + case-insensitive compare). */
export const EXCLUDED_SHEETS = [
  "LIST", "Sheet2", "B.O", "CMB", "SET GARDAN", "SET PINION", "SET C. DIFF", "URAI C.ASSY",
];

/** Category name derivation from a sheet name — one place to change if the naming convention
 *  (e.g. "S. KANTOR") is ever renamed. Sheet names in the source file carry trailing spaces. */
const CATEGORY_FROM_SHEET = (sheetName) => String(sheetName || "").trim();

function isExcludedSheet(name) {
  const n = String(name || "").trim().toUpperCase();
  return EXCLUDED_SHEETS.some((ex) => ex.trim().toUpperCase() === n);
}

/** blank(v): the export's convention for "no value" — empty, whitespace-only, or a bare "-". */
function blank(v) {
  if (v == null) return true;
  const s = String(v).trim();
  return s === "" || s === "-";
}

/** Numeric cell → number. Blank/"-"/unparseable/Date → 0. Accepts negatives. */
function num(v) {
  if (v instanceof Date) return 0;
  if (blank(v)) return 0;
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
}

function cellText(v) {
  return v == null ? "" : String(v).trim();
}

function isHeaderRow(row) {
  if (!Array.isArray(row)) return false;
  const norm = row.map((c) => cellText(c).toUpperCase());
  return norm.includes("KODE BARANG") && norm.includes("SISA") && norm.includes("IJM");
}

function findHeaderRowIndex(rows) {
  for (let i = 0; i < rows.length; i++) {
    if (isHeaderRow(rows[i])) return i;
  }
  return -1;
}

/** A mid-sheet repeat of the header row (col 1 === "KODE BARANG"). Skipped silently. */
function isRepeatedHeaderRow(row) {
  return cellText(row?.[1]).toUpperCase() === "KODE BARANG";
}

/** Count of alphanumeric characters only — the smart-split length heuristic ignores spaces,
 *  slashes, and punctuation so "MB 241909" and "MB241909" measure the same. */
function alnumLen(s) {
  return String(s).replace(/[^A-Za-z0-9]/g, "").length;
}

/**
 * Smart split for a KODE BARANG cell:
 *   - no "/" → the whole trimmed cell is ONE code, not reviewed.
 *   - has "/" AND every "/"-separated trimmed part has alnumLen >= 6 → split into those parts.
 *   - has "/" but at least one part is too short (a real code fragment like "88CA", or a
 *     non-market-code annotation) → kept as ONE code (the whole trimmed cell) — splitting on
 *     every slash is wrong for compound part numbers like "58360/460-45001".
 *   - EVERY cell containing "/" is flagged for human review regardless of which way the
 *     heuristic landed (the split path is a best-effort guess, not always correct).
 */
function smartSplitMarketCode(raw) {
  const trimmed = cellText(raw);
  if (!trimmed.includes("/")) {
    return { codes: trimmed ? [trimmed] : [], decision: null };
  }
  const parts = trimmed.split("/").map((p) => p.trim()).filter((p) => p !== "");
  const allLongEnough = parts.length > 0 && parts.every((p) => alnumLen(p) >= 6);
  if (allLongEnough) {
    return { codes: parts, decision: "split" };
  }
  return { codes: [trimmed], decision: "kept" };
}

/**
 * Parse a workbook (already-extracted sheets) into app-shaped import data.
 * Pure: deterministic, no I/O, no Date.now/Math.random. No internalCode is minted here.
 *
 * @param {{name: string, rows: any[][]}[]} sheets
 * @param {{includeSheets?: Iterable<string>}} [options] — filter-early opt-in (task B1a).
 *   Absent/undefined: identical to calling with no options at all (the pre-B1a behavior,
 *   preserved exactly). Provided: a sheet is processed ONLY if its trimmed/uppercased name is
 *   in the set — chosen sheets BYPASS the hardcoded EXCLUDED_SHEETS name-check (that's the
 *   whole point of opting in) but still MUST pass findHeaderRowIndex like any other sheet; a
 *   chosen sheet that fails header detection lands on the existing no-header skip/warn path,
 *   never imported as garbage. An unchosen sheet is skipped silently — no warning, no
 *   skippedSheets entry — a deliberate user exclusion, not an error.
 * @returns {{
 *   items: object[], categories: {name: string}[], cars: {name: string}[],
 *   reviewList: {sheet: string, rowIndex: number, raw: string, decision: "split"|"kept", codes: string[], itemIndex: number}[],
 *   warnings: {sheet: string, rowIndex?: number, reason: string}[],
 *   skippedSheets: {sheet: string, reason: "excluded"|"no-header"}[],
 *   stats: {sheets: number, items: number, transactionsSkipped: number, headings: number, headerRepeats: number},
 * }}
 */
export function parseWorkbook(sheets, options = {}) {
  const { includeSheets } = options;
  const includeSet = includeSheets
    ? new Set([...includeSheets].map((n) => String(n || "").trim().toUpperCase()))
    : null;

  const items = [];
  const categories = [];
  const seenCategories = new Set();
  const reviewList = [];
  const warnings = [];
  const skippedSheets = [];
  const stats = { sheets: 0, items: 0, transactionsSkipped: 0, headings: 0, headerRepeats: 0 };

  for (const sheet of sheets || []) {
    const name = sheet?.name;
    const rows = sheet?.rows || [];

    if (includeSet) {
      // Filter-early opt-in: only a chosen sheet is processed at all. Unchosen → silent skip
      // (no warning — the user deliberately left it out). Chosen → bypass the name-based
      // EXCLUDED_SHEETS check entirely and fall straight to the same header-detection gate
      // every other sheet goes through below.
      if (!includeSet.has(String(name || "").trim().toUpperCase())) continue;
    } else if (isExcludedSheet(name)) {
      skippedSheets.push({ sheet: CATEGORY_FROM_SHEET(name), reason: "excluded" });
      continue;
    }

    const headerIndex = findHeaderRowIndex(rows);
    if (headerIndex === -1) {
      const trimmedName = CATEGORY_FROM_SHEET(name);
      warnings.push({ sheet: trimmedName, reason: "no-header" });
      skippedSheets.push({ sheet: trimmedName, reason: "no-header" });
      continue;
    }

    const category = CATEGORY_FROM_SHEET(name);
    if (!seenCategories.has(category)) {
      seenCategories.add(category);
      categories.push({ name: category });
    }
    stats.sheets += 1;

    for (let r = headerIndex + 1; r < rows.length; r++) {
      const row = rows[r] || [];

      if (isRepeatedHeaderRow(row)) {
        stats.headerRepeats += 1;
        continue;
      }

      const tgl = row[4];
      if (!blank(tgl)) {
        // Transaction row (Date instance or a non-blank string) — its balance is a formula
        // in the source, not authoritative data. Skip.
        stats.transactionsSkipped += 1;
        continue;
      }

      const kode = row[1];
      const jenis = row[2];
      if (!blank(kode) && blank(jenis)) {
        // Section heading within the sheet (e.g. "ADJUSTER ( SETELAN REM )") — not an item.
        stats.headings += 1;
        continue;
      }
      if (blank(kode) || blank(jenis)) {
        // Fully blank row or a stray annotation — skip silently.
        continue;
      }

      // ── MASTER row → item ────────────────────────────────────────────────────
      const sisaCell = row[9];
      const ijmCell = row[10];
      if (kode instanceof Date || sisaCell instanceof Date || ijmCell instanceof Date) {
        warnings.push({ sheet: category, rowIndex: r, reason: "suspicious-cell" });
      }
      if (blank(sisaCell) && blank(ijmCell)) {
        warnings.push({ sheet: category, rowIndex: r, reason: "no-stock" });
      }

      const rawKode = cellText(kode);
      const { codes, decision } = smartSplitMarketCode(rawKode);
      if (rawKode.includes("/")) {
        // itemIndex: items.length at push time equals this row's item's eventual index in
        // `items` (pushed right below) — the stable link importCommit.applyReviewDecisions
        // needs to recompute a flipped row's marketCodes back onto the right item.
        reviewList.push({ sheet: category, rowIndex: r, raw: rawKode, decision, codes, itemIndex: items.length });
      }

      const ijm = num(ijmCell);
      const sisa = num(sisaCell);
      const notes = blank(row[11]) ? "" : cellText(row[11]);

      items.push({
        marketCodes: codes,
        name: cellText(jenis),
        fitment: parseFitment(row[3]),
        category,
        tipe: null,
        stokPusat: ijm,
        stokCabang: sisa - ijm,
        isSet: false,
        notes,
        minStock: null,
      });
    }
  }

  // Cars: distinct fitment names across all items, first-seen order, case-insensitive dedupe —
  // same rule as the v5->v6 storage migration. Spelling variants stay separate on purpose.
  const cars = [];
  const seenCars = new Set();
  for (const it of items) {
    for (const car of it.fitment) {
      const nm = car.trim();
      if (!nm) continue;
      const key = nm.toUpperCase();
      if (seenCars.has(key)) continue;
      seenCars.add(key);
      cars.push({ name: nm });
    }
  }

  stats.items = items.length;

  return { items, categories, cars, reviewList, warnings, skippedSheets, stats };
}
